import downTriangleBlack from 'img/down-triangle-black.svg'
import React, { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import styles from 'css/components/search-results.module.css'
import { LessonLibraryTable } from 'lesson-library/table'
import { useGetLessonsSearchQuery } from 'util/rtk-api'

export const SearchFilter = ({
	ariaLabel,
	label,
	value,
	options,
	onChange,
	onClear,
	isSort = false,
}) => {
	const formattedOptions = Array.isArray(options)
		? options.map(option => [option, option])
		: Object.entries(options)
	const selectRef = useRef()

	return (
		<>
			<select
				ref={selectRef}
				id={isSort ? 'sort' : ''}
				className={value ? styles.selected : ''}
				aria-label={ariaLabel}
				value={value}
				onChange={onChange}
			>
				<option value="" hidden>{label}</option>
				{formattedOptions.map(([value, text], i) =>
					<option key={value + i} value={value}>{text}</option>
				)}
			</select>
			<span
				className={`${styles.removeFilter} ${value && !isSort ? styles.show : ''}`}
				onClick={onClear}
			/>
		</>
	)
}

export const SearchResultRow = ({
	lessonId,
	name,
	lastViewed,
	lastModified,
	status,
}) => {
	const formatDate = timestamp => {
		const date = new Date(timestamp)
		const [_, month, day, year] = date.toDateString().split(' ')

		return `${month} ${day}, ${year}`
	}

	return (
		<tr>
			<td id="name">
				<Link
					to={`/lesson-builder?lessonId=${lessonId}`}
				>
					{name}
				</Link>
			</td>
			<td id="lastViewed">{
				formatDate(lastViewed)
			}</td>
			<td id="lastModified">{
				formatDate(lastModified)
			}</td>
			<td id="status">{status}</td>
		</tr>
	)
}

export const SearchResults = ({ header, query, classId }) => {
	const [grade, setGrade] = useState()
	const [subject, setSubject] = useState()
	const [period, setPeriod] = useState()
	const [sortCol, setSortCol] = useState('lastModified')
	const [sortDir, setSortDir] = useState(false) // false = descending for date columns (newest first)
	const { data, isLoading, isError, refetch } = useGetLessonsSearchQuery({classId})

	const isReady = Array.isArray(data?.lessons) && !isLoading
	const safeLessons = Array.isArray(data?.lessons) ? data.lessons : []

	const searchLessons = lessons => {
		if (!query) return lessons

		const regex = // For non-sequential matches
			new RegExp(query.split("").join(".*"), "i")

		return lessons.map(lesson => {
			let sequentialScore = 0
			let index = 0

			const name = lesson.name.toLowerCase()

			// Check for sequential match
			for (let char of query) {
				index = name.indexOf(char, index)

				// If a character isn't found in sequence, reset score
				if (index === -1) {
					sequentialScore = 0
					break
				}

				sequentialScore += 2 // Higher weight
				index++
			}

			// Check for a non-sequential match
			const regexScore = regex.test(name) ? 1 : 0 // Lower weight

			const score = sequentialScore + regexScore
			console.log(name, sequentialScore, regexScore, query, regex)

			return score > 0 && { lesson, score }
		})
			.filter(entry => entry?.score > 0)
			.sort((a, b) => b.score - a.score)
			.map(entry => entry.lesson)
	}

	const filterLessons = lessons => {
		const filtered = lessons
			.filter(lesson => !grade || lesson.grades?.includes(grade))
			.filter(lesson => !subject || lesson.subject === subject)
			.filter(lesson => !period || lesson.period === period)

		return searchLessons(filtered)
			.toSorted((a, b) => {
				const isDateColumn = sortCol === 'lastViewed' || sortCol === 'lastModified'
				const effectiveSortDir = isDateColumn ? !sortDir : sortDir
				
				if (effectiveSortDir) {
					return a[sortCol] > b[sortCol] ? -1 : 1
				} else {
					return a[sortCol] < b[sortCol] ? -1 : 1
				}
			})
	}

	return (
		<div className={styles.searchResults}>
			{header && (
				<div className={styles.searchResultsHeader}>
					{header}
				</div>
			)}
			<div className={styles.sectionContainer}>
				{/* err-11: a 5xx used to render as an empty "Loading..." forever —
				    a teacher could think their library was gone */}
				{isError ? (
					<div>
						<h2>We couldn't load your lessons</h2>
						<p>Your lessons are safe. Check your connection, then <a href="#retry" onClick={e => { e.preventDefault(); refetch() }}>try again</a>.</p>
					</div>
				) : !isReady ? (
					<div>
						<h2>
							Loading...
						</h2>
					</div>
				) : (
					<>
						<div className={styles.tableOptions}>
							<SearchFilter
								ariaLabel="grade filter"
								label="Grade"
								value={grade}
								options={["Kindergarten"].concat([...Array(12)].map((_, i) => String(i + 1)))}
								onChange={({ target }) => setGrade(target.value)}
								onClear={() => setGrade('')}
							/>
							<SearchFilter
								ariaLabel="subject filter"
								label="Subject"
								value={subject}
								options={[
									'Science', 'Math', 'ELA',
									'Social Studies', 'Other',
								]}
								onChange={({ target }) => setSubject(target.value)}
								onClear={() => setSubject('')}
							/>
							<SearchFilter
								ariaLabel="period filter"
								label="Period"
								value={period}
								options={['All Day', '1', '2', '3', '4', '5', '6', '7', '8']}
								onChange={({ target }) => setPeriod(target.value)}
								onClear={() => setPeriod('')}
							/>
							<span
								id="clear-filters"
								className={[styles.clearFilters, (grade || subject || period) ? styles.show : ''].join(' ')}
								onClick={() => {
									setGrade('')
									setSubject('')
									setPeriod('')
								}}
							>
								Clear filters
							</span>
							<span className={styles.spacer}></span>
							<div
								id="sort-container"
							>
								<span
									id="cancel-sort"
									aria-label="cancel sort"
									onClick={() => {
										setSortCol('')
									}}
								/>
								<SearchFilter
									ariaLabel="sort by column"
									label="Sort column"
									value={sortCol}
									options={{
										name: 'Name',
										lastViewed: 'Last viewed',
										lastModified: 'Last modified',
										status: 'Status',
									}}
									onChange={({ target }) => {
										setSortCol(target.value)
										setSortDir(false)
									}}
									onClear={() => {
										setSortCol('')
										setSortDir(false)
									}}
									isSort={true}
								/>
							</div>
						</div>
						<LessonLibraryTable lessons={filterLessons(safeLessons)} />
					</>
				)}
			</div>
		</div>
	)
}
